// Eskom PARCC — AI Assurance report.
//
// The POC's headline surface: across every transaction the engine has reviewed,
// how far did its proposed verdicts agree with the reviewer's FINAL call, and
// what has it covered? Joins the cross-transaction AI feed (newest completed
// review per transaction, quote-free) against the reviewer's persisted CES rows,
// per criterion, via the pure computeAgreement — nothing is recomputed by the
// engine and nothing is spent. Report-lead roles only (gated in canAccessRoute
// AND the server endpoint), because it crosses divisions.
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  KpiCard,
  KpiGrid,
  DataTable,
  LoadingState,
  ErrorState,
  EmptyState,
  type Column,
} from '../../components/ui';
import { useAiReviewLatestAll } from '../../hooks/useAiReview';
import { useCesRowsByRef, useTransactions } from '../../hooks';
import {
  computeAgreement,
  sumAgreement,
  type AgreementResult,
} from '../../domain/aiAgreement';
import type { AiProposal } from '../../lib/adminApi';

interface AssuranceRow {
  referenceNo: string;
  model: string | null;
  reviewedAt: string;
  division: string;
  supplier: string;
  total: number;
  compared: number;
  agreed: number;
  disagreed: number;
  pending: number;
  rate: number | null;
}

function parseProposals(json: string): AiProposal[] {
  try {
    return JSON.parse(json || '[]') as AiProposal[];
  } catch {
    return [];
  }
}

const pct = (r: number | null): string => (r == null ? '—' : `${Math.round(r * 100)}%`);

export function AiAssurancePage(): React.JSX.Element {
  const aiQ = useAiReviewLatestAll();
  const cesQ = useCesRowsByRef();
  const txQ = useTransactions();

  const { rows, totals, proposalsTotal } = useMemo(() => {
    const cesByRef = cesQ.data ?? new Map();
    const txByRef = new Map((txQ.data ?? []).map((t) => [t.referenceNo, t]));
    const results: AgreementResult[] = [];
    const built: AssuranceRow[] = [];
    let proposals = 0;
    for (const s of aiQ.data ?? []) {
      const ag = computeAgreement(parseProposals(s.proposalsJson), cesByRef.get(s.referenceNo) ?? []);
      results.push(ag);
      proposals += ag.total;
      const tx = txByRef.get(s.referenceNo);
      built.push({
        referenceNo: s.referenceNo,
        model: s.model,
        reviewedAt: s.createdAt,
        division: tx?.division ?? '',
        supplier: tx?.supplier ?? '',
        total: ag.total,
        compared: ag.compared,
        agreed: ag.agreed,
        disagreed: ag.disagreed,
        pending: ag.pendingHuman,
        rate: ag.agreementRate,
      });
    }
    return { rows: built, totals: sumAgreement(results), proposalsTotal: proposals };
  }, [aiQ.data, cesQ.data, txQ.data]);

  if (aiQ.isLoading || cesQ.isLoading || txQ.isLoading) return <LoadingState message="Loading AI assurance…" />;
  // Guard EVERY query the agreement number depends on: a failed CES or transaction
  // fetch would otherwise render the headline agreement over half-missing data —
  // an authoritative-looking figure that is simply wrong.
  const assuranceError = aiQ.error ?? cesQ.error ?? txQ.error;
  if (assuranceError) {
    return (
      <ErrorState
        message="Could not load the AI review summary."
        detail={assuranceError instanceof Error ? assuranceError.message : undefined}
      />
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState message="No AI reviews yet — once a transaction has a completed AI review, its coverage and agreement will appear here." />
    );
  }

  const columns: Column<AssuranceRow>[] = [
    {
      key: 'referenceNo',
      header: 'Transaction',
      sortable: true,
      render: (r) => <Link to={`/ces/${encodeURIComponent(r.referenceNo)}`}>{r.referenceNo}</Link>,
    },
    { key: 'division', header: 'Division', sortable: true, render: (r) => r.division || '—' },
    {
      key: 'reviewedAt',
      header: 'Reviewed',
      sortable: true,
      accessor: (r) => r.reviewedAt,
      render: (r) =>
        new Date(r.reviewedAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }),
    },
    { key: 'model', header: 'Model', sortable: true, render: (r) => r.model ?? '—' },
    {
      key: 'agreed',
      header: 'Matched',
      sortable: true,
      align: 'right',
      accessor: (r) => r.agreed,
      render: (r) => `${r.agreed}/${r.compared}`,
    },
    { key: 'disagreed', header: 'Overridden', sortable: true, align: 'right', accessor: (r) => r.disagreed },
    { key: 'pending', header: 'Pending', sortable: true, align: 'right', accessor: (r) => r.pending },
    {
      key: 'rate',
      header: 'Agreement',
      sortable: true,
      align: 'right',
      // Un-compared (no criteria decided both sides) sorts last on an ascending
      // "lowest agreement first" default.
      accessor: (r) => (r.rate == null ? -1 : r.rate),
      render: (r) => pct(r.rate),
    },
  ];

  return (
    <div className="ai-assurance">
      <KpiGrid>
        <KpiCard label="Transactions AI-reviewed" value={totals.transactions} sub="with a completed engine review" />
        <KpiCard
          label="Overall agreement"
          value={pct(totals.agreementRate)}
          sub={`${totals.agreed}/${totals.compared} criteria decided on both sides`}
        />
        <KpiCard label="Proposals" value={proposalsTotal} sub="criterion verdicts proposed" />
        <KpiCard
          label="Overrides"
          value={totals.disagreed}
          sub="where the reviewer disagreed with the engine"
        />
      </KpiGrid>
      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.referenceNo}
        initialSort={{ key: 'rate', dir: 'asc' }}
        emptyMessage="No AI reviews yet."
      />
    </div>
  );
}
